// COVER TEXT sizing maths (owner 2026-10-30): « write a phrase and make it
// big », « ajuster à la boîte ». Pure (the caller passes a measure function —
// a canvas in the editor, a fake in the tests).
//
// UNITS. Everything here is in « face units »: the face is 400 units wide (the
// editor's reference — a text's `fontSize` is px on a 400-px-wide face, drawn
// at `fontSize/4 cqi`; the print draws fontSize × faceW / 400). A box of
// width W % is W × 4 units wide; height H % is H/100 × 400 / faceAspect units
// tall. The SAME wrap the print uses (pdfTextLayout.wrapTextLines) breaks the
// lines, so the editor's « fits / does not fit » is the print's.

import { wrapTextLines, textLineHeight } from './pdfTextLayout';

export const FACE_UNITS = 400;

export interface TextBoxUnits { w: number; h: number }

/** The element's box in face units. `faceAspect` = face width / height. */
export function boxUnits(el: { width: number; height: number }, faceAspect: number): TextBoxUnits {
  return { w: (el.width / 100) * FACE_UNITS, h: ((el.height / 100) * FACE_UNITS) / faceAspect };
}

/** `measureAt(size)` returns a function measuring a string at that font size (face units). */
export type MeasureAt = (size: number) => (s: string) => number;

/** Lines and block height of a text at `size` in a box `boxW` units wide. */
export function textBlock(text: string, size: number, boxW: number, lineHeight: number, measureAt: MeasureAt): { lines: string[]; height: number } {
  const lines = wrapTextLines(text, boxW, measureAt(size));
  return { lines, height: lines.length * size * lineHeight };
}

/** The height of the words in % of the face height (for the overflow flag and the safe zone). */
export function textHeightPct(
  el: { text?: string; fontSize?: number; width: number; height: number; lineHeight?: number },
  faceAspect: number,
  measureAt: MeasureAt,
): number {
  const b = boxUnits(el, faceAspect);
  const { height } = textBlock(el.text ?? '', el.fontSize ?? 24, b.w, textLineHeight(el), measureAt);
  return (height * faceAspect / FACE_UNITS) * 100;
}

/**
 * « Ajuster à la boîte »: the largest font size at which the text fits the box —
 * every line as wide as the box at most WITHOUT cutting a word (a word wider
 * than the box would be broken letter by letter), and every line inside its
 * height. Binary search over [min, max]; result rounded down to 0.1.
 */
export function fitFontSize(o: {
  text: string;
  box: TextBoxUnits;
  lineHeight: number;
  measureAt: MeasureAt;
  min?: number;
  max?: number;
}): number {
  const min = o.min ?? 2;
  const max = o.max ?? 3000;
  const words = o.text.split(/\s+/).filter(Boolean);
  const fits = (size: number) => {
    const m = o.measureAt(size);
    if (words.some((w) => m(w) > o.box.w)) return false;
    return textBlock(o.text, size, o.box.w, o.lineHeight, o.measureAt).height <= o.box.h;
  };
  if (!o.text.trim() || !fits(min)) return min;
  let lo = min, hi = max;
  if (fits(hi)) return hi;
  for (let i = 0; i < 40 && hi - lo > 0.05; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid; else hi = mid;
  }
  return Math.floor(lo * 10) / 10;
}

/** A canvas-backed MeasureAt for a font (browser only). Font weight/style as CSS. */
export function canvasMeasureAt(family: string, weight: string | number = 600, style = 'normal'): MeasureAt | null {
  if (typeof document === 'undefined') return null;
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;
  return (size: number) => {
    ctx.font = `${style} ${weight} ${size}px ${family}`;
    return (s: string) => ctx.measureText(s).width;
  };
}
